import { useCallback, useEffect, useRef, useState } from "react";
import type { OwnerListQuery, OwnerListPage } from "@stackje/site-builder";
import { requestTimeout } from "../lib/requestTimeout";

/** Aborted filter requests cannot replace newer rows. Loading more keeps the same item objects selected. */
export function useOwnerPage<T extends { id: string }>(load: (site: string, query: OwnerListQuery, signal?: AbortSignal) => Promise<OwnerListPage<T>>, site: string, query: OwnerListQuery, revision: unknown) {
  const key = JSON.stringify(query);
  const [rows, setRows] = useState<T[]>([]), [total, setTotal] = useState(0), [hasMore, setHasMore] = useState(false), [busy, setBusy] = useState(true), [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadedSite = useRef(site);
  const current = useRef<{ controller: AbortController; next: number; pending: boolean; loaded: boolean } | null>(null);
  const fetchPage = useCallback(async (state: NonNullable<typeof current.current>, page: number) => {
    if (state.pending) return;
    state.pending = true; setBusy(true); setLoadingMore(page > 0); setError(null);
    try {
      const result = await requestTimeout(signal => load(site, { ...JSON.parse(key) as OwnerListQuery, page }, signal), state.controller.signal);
      if (state.controller.signal.aborted) return;
      setRows(before => page === 0 ? result.rows : [...before, ...result.rows.filter(r => !before.some(item => item.id === r.id))]);
      setTotal(result.total); state.loaded = true; state.next = result.page + 1; setHasMore(result.rows.length > 0 && state.next * 50 < result.total);
    } catch (e) { if (!state.controller.signal.aborted) setError(e instanceof Error ? e.message : "The list couldn't be loaded."); }
    finally { state.pending = false; if (!state.controller.signal.aborted) { setBusy(false); setLoadingMore(false); } }
  }, [load, site, key]);
  useEffect(() => {
    const state = { controller: new AbortController(), next: 0, pending: false, loaded: false }; current.current = state;
    if (loadedSite.current !== site) { setRows([]); setTotal(0); loadedSite.current = site; }
    setHasMore(false); setBusy(true); setLoadingMore(false); setError(null);
    const timer = window.setTimeout(() => void fetchPage(state, 0), 150);
    return () => { window.clearTimeout(timer); state.controller.abort(); };
  }, [fetchPage, revision, site]);
  const more = () => { const state = current.current; if (state && state.loaded) void fetchPage(state, state.next); };
  const retry = () => { const state = current.current; if (state) void fetchPage(state, state.next); };
  return { rows: loadedSite.current === site ? rows : [], total: loadedSite.current === site ? total : 0, busy, loadingMore, refreshing: busy && !loadingMore, error, more, retry, hasMore };
}

/** A row stores the concurrency version the owner selected, even when another page/filter is loaded. */
export function toggleRows<T extends { id: string }>(selected: T[], rows: T[], checked: boolean): T[] {
  const ids = new Set(rows.map(r => r.id));
  return checked ? [...selected.filter(r => !ids.has(r.id)), ...rows] : selected.filter(r => !ids.has(r.id));
}
